import { Route } from '@angular/router';
import { OktaAuthGuard } from '@okta/okta-angular';
import { FlOktaCallbackComponent } from 'src/lib/fl-identity/components/fl-okta-callback/fl-okta-callback.component';
import { PilotDashboardPageComponent } from './dashboards/pilot-dashboard/components/pilot-dashboard-page/pilot-dashboard-page.component';
import { DutyEditPageComponent } from './duty-logs/components/duty-edit-page/duty-edit-page.component';
import { DutyEditRetroPageComponent } from './duty-logs/components/duty-edit-retro-page/duty-edit-retro-page.component';
import { DutyLogIndexPageComponent } from './duty-logs/components/duty-log-index-page/duty-log-index-page.component';
import { DutyOffPageComponent } from './duty-logs/components/duty-off-page/duty-off-page.component';
import { DutyOnPageComponent } from './duty-logs/components/duty-on-page/duty-on-page.component';
import { CreateFlightLogPageComponent } from './flight-logs/components/create-flight-log-page/create-flight-log-page.component';
import { EditFlightLogPageComponent } from './flight-logs/components/edit-flight-log-page/edit-flight-log-page.component';
import { FlightLogIndexPageComponent } from './flight-logs/components/flight-log-index-page/flight-log-index-page.component';
import { ViewFlightLogPageComponent } from './flight-logs/components/view-flight-log-page/view-flight-log-page.component';
import { LogoutPageComponent } from './login/components/logout-page/logout-page.component';
import { MaintenanceComparisonPageComponent } from './maintenance/components/maintenance-comparison-page/maintenance-comparison-page.component';
import { MaintenanceLogsPageComponent } from './maintenance/components/maintenance-logs-page/maintenance-logs-page.component';
import { Four04PageComponent } from './pages/components/four04-page/four04-page.component';
import { UnauthorizedPageComponent } from './pages/components/unauthorized-page/unauthorized-page.component';
import { PilotAddPageComponent } from './pilots/components/pilot-add-page/pilot-add-page.component';
import { PilotDetailPageComponent } from './pilots/components/pilot-detail-page/pilot-detail-page.component';
import { PilotListPageComponent } from './pilots/components/pilot-list-page/pilot-list-page.component';
import { PilotManagePageComponent } from './pilots/components/pilot-manage-page/pilot-manage-page.component';
import { AirmanFlightDutyReportPageComponent } from './reports/components/airman-flight-duty-report-page/airman-flight-duty-report-page.component';
import { ReportIndexPageComponent } from './reports/components/report-index-page/report-index-page.component';
import { AirCarrierResolverService } from './shared/air-carrier-resolver.service';
import { UserGuard } from './shared/authentication/user.guard';
import { AirCarrierGuard } from './shared/authorization/air-carrier.guard';
import { FlightLogUserGuard } from './shared/authorization/flight-log-user.guard';
import { maintenancePermissions } from './shared/authorization/maintenance/maintenance-permissions.service';
import { PermissionsGuard } from './shared/authorization/permissions.guard';
import { PrdUserGuard } from './shared/authorization/prd-user.guard';
import { DefaultLayoutComponent } from './shared/components/default-layout/default-layout.component';
import { FlightLogActiveGuard } from './shared/flight-log-api/flight-logs/flight-log-active.guard';
import { TransmissionRunDetailListPageComponent } from './transmission-runs/components/transmission-run-detail-list-page/transmission-run-detail-list-page.component';
import { TransmissionRunListPageComponent } from './transmission-runs/components/transmission-run-list-page/transmission-run-list-page.component';
import { UserActivityReportListPageComponent } from './user-activity-reports/components/user-activity-report-list-page/user-activity-report-list-page.component';
import { PrdPermissionsPageComponent } from './prd-permissions/components/prd-permissions-page/prd-permissions-page.component';

export const routes: Route[] = [
  {
    path: '',
    component: DefaultLayoutComponent,
    canActivateChild: [UserGuard, OktaAuthGuard],
    data: {
      title: 'FlightLog - TCS',
    },
    children: [
      {
        path: '',
        canActivate: [PrdUserGuard],
        component: PilotDashboardPageComponent,
        data: {
          title: 'Pilot Dashboard',
        },
      },
      {
        path: 'units',
        canActivate: [FlightLogUserGuard],
        loadChildren: () => import('./units/units.module').then((m) => m.UnitsModule),
      },
      {
        path: 'flight-logs',
        canActivate: [FlightLogUserGuard],
        children: [
          {
            path: '',
            component: FlightLogIndexPageComponent,
            data: { title: 'My Flights' },
          },
          {
            path: 'create',
            component: CreateFlightLogPageComponent,
            data: { title: 'Create Flight Log' },
          },
          {
            path: ':id/edit',
            component: EditFlightLogPageComponent,
            data: { title: 'Edit Flight Log' },
            canActivate: [FlightLogActiveGuard],
          },
          {
            path: ':id',
            component: ViewFlightLogPageComponent,
            data: { title: 'Flight Log Details' },
          },
        ],
      },
      {
        path: 'dashboards',
        canActivate: [FlightLogUserGuard],
        children: [
          {
            path: 'pilot',
            component: PilotDashboardPageComponent,
            data: { title: 'Pilot Dashboard' },
          },
        ],
      },
      {
        path: 'duty-logs',
        canActivate: [FlightLogUserGuard],
        children: [
          {
            path: '',
            component: DutyLogIndexPageComponent,
            data: { title: 'Duty Logs' },
          },
          {
            path: 'retro',
            component: DutyEditRetroPageComponent,
            data: { title: 'Duty Log' },
          },
          {
            path: 'duty-on/:id',
            component: DutyOnPageComponent,
            data: { title: 'Duty On' },
          },
          {
            path: 'duty-on',
            component: DutyOnPageComponent,
            data: { title: 'Duty On' },
          },
          {
            path: 'duty-off',
            component: DutyOffPageComponent,
            data: { title: 'Duty Off' },
          },

          {
            path: ':id',
            component: DutyEditPageComponent,
            data: { title: 'Duty Edit' },
          },
        ],
      },
      {
        path: 'reports',
        component: ReportIndexPageComponent,
        canActivate: [FlightLogUserGuard],
        data: { title: 'Reports' },
        children: [
          {
            path: 'airman-flight-duty-hours',
            component: AirmanFlightDutyReportPageComponent,
            data: { title: 'Airman Flight and Duty Hours' },
          },
        ],
      },
      {
        path: 'maintenance',
        canActivate: [FlightLogUserGuard],
        data: { title: 'Maintenance' },
        children: [
          {
            path: '',
            component: MaintenanceLogsPageComponent,
            data: { title: 'Maintenance Logs' },
          },
          {
            path: 'parameters',
            canActivateChild: [PermissionsGuard],
            data: {
              title: 'Parameter Comparison',
              permissions: [maintenancePermissions.routeMaintenanceParameters],
            },
            children: [
              {
                path: '',
                component: MaintenanceComparisonPageComponent,
              },
              {
                path: ':aircraftId',
                component: MaintenanceComparisonPageComponent,
              },
            ],
          },
        ],
      },
      {
        path: 'prd',
        data: { title: 'PRD' },
        canActivateChild: [AirCarrierGuard],
        children: [
          {
            path: 'permissions',
            component: PrdPermissionsPageComponent,
            data: { title: 'Permissions' },
          },
          {
            path: ':airCarrierAbbreviation',
            canActivate: [AirCarrierGuard],
            resolve: { airCarrier: AirCarrierResolverService },
            children: [
              {
                path: 'pilots',
                data: { title: 'Pilots' },
                children: [
                  { path: '', component: PilotListPageComponent },
                  {
                    path: 'add',
                    component: PilotAddPageComponent,
                  },
                  {
                    path: ':id/edit',
                    component: PilotManagePageComponent,
                  },
                  {
                    path: ':id',
                    component: PilotDetailPageComponent,
                  },
                ],
              },
              {
                path: 'transmission-runs',
                data: { title: 'Transmission Runs' },
                children: [
                  { path: '', component: TransmissionRunListPageComponent },
                  {
                    path: ':id',
                    component: TransmissionRunDetailListPageComponent,
                  },
                ],
              },
              {
                path: 'user-activity-reports',
                data: { title: 'User Activity Reports' },
                children: [{ path: '', component: UserActivityReportListPageComponent }],
              },
            ],
          },
        ],
      },
      {
        path: 'unauthorized',
        component: UnauthorizedPageComponent,
        data: {
          title: 'Unauthorized',
        },
      },
    ],
  },

  {
    path: 'implicit/callback',
    component: FlOktaCallbackComponent,
  },
  {
    path: 'logout',
    component: LogoutPageComponent,
    data: { title: 'Logout' },
  },

  {
    path: '**',
    component: Four04PageComponent,
    data: { title: 'Not Found' },
  },
];
